import { InteractionType } from "@azure/msal-browser";
import { BrowserRouter, Outlet, Route, Routes } from "react-router-dom";
import { Buffer } from 'buffer';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal, useMsalAuthentication } from "@azure/msal-react";
import { useEffect, useMemo, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import { AppDispatch, RootState } from "./configureStore";
import { SET_MOBILE, clearError, clearSuccessful, healthcheck } from "./redux/reducer/status";

import CheckOperations from "./components/templates/CheckOperations";
import TaxReturns from "./components/templates/TaxReturns";
import ReturnsApp from "./components/templates/ReturnsApp";
import ReturnOps from "./components/templates/ReturnOps";
import TaskSelector from "./components/organisms/TaskSelector";
import Automation from "./components/templates/Automation";
import IdentifyPages from "./components/templates/IdentifyPages";
import ExtractData from "./components/templates/ExtractData";
import MatchForms from "./components/templates/MatchForms";
import UIManagement from "./components/templates/UIManagement";
import PromptManagement from "./components/templates/PromptManagement";
import ConfigManagement from "./components/templates/ConfigManagement";
import WebhookDebugger from "./components/templates/WebhookDebugger";
import InFlightMonitor from "./components/templates/InFlightMonitor";
import { developerList } from "./utils";
import { SET_IS_DEV } from "./redux/reducer/ui";

interface OperationsAppParams {
}

const Pages = ({ id }: { id: string }) => {
    const dispatch = useDispatch<AppDispatch>();
    useEffect(() => {
        const runHealthcheck = () => {
            if (sessionStorage.getItem('ACCESS_TOKEN')) {
                dispatch(healthcheck({}));
            }
        }

        const interval = setInterval(runHealthcheck, 60000);

        runHealthcheck();

        return () => {
            clearInterval(interval);
        }
    }, [dispatch])

    return (
        <BrowserRouter>
            <Routes>
                <Route index element={<TaskSelector />} />
                <Route path="review" element={<CheckOperations id={id} />} />
                <Route path="automation" element={<Automation />} />
                <Route path="ui-management" element={<UIManagement />} />
                <Route path="prompt-management" element={<PromptManagement />} />
                <Route path="config-management" element={<ConfigManagement />} />
                <Route path="webhook-debugger" element={<WebhookDebugger />} />
                <Route path="returns" element={<ReturnsApp />}>
                    <Route index element={<TaxReturns />} />
                    <Route path="identify" element={<Outlet />}>
                        <Route path=":loanId" element={<IdentifyPages />} />
                    </Route>
                    <Route path="extract" element={<Outlet />}>
                        <Route path=":loanId" element={<ExtractData />} />
                    </Route>
                    <Route path="grouping" element={<Outlet />}>
                        <Route path=":loanId" element={<MatchForms />} />
                    </Route>
                    <Route path="processing" element={<Outlet />}>
                        <Route path=":loanId" element={<div>processing</div>} />
                    </Route>
                </Route>
                <Route path="returns/inflight" element={<InFlightMonitor />} />
                <Route path="return-ops" element={<ReturnOps />} />
            </Routes>
        </BrowserRouter>
    );
};

const OperationsApp = ({
}: OperationsAppParams) => {
    const request = {
        scopes: [`api://${import.meta.env.VITE_TOKEN_AUDIENCE}/api`],
    };
    const { instance, accounts } = useMsal();

    const dispatch = useDispatch<AppDispatch>();
    const error = useSelector<RootState, string | undefined>(({ status }) => status.error);
    const successful = useSelector<RootState, string | undefined>(({ status }) => status.successful);
    const [open, setOpen] = useState<boolean>(false);

    useEffect(() => {
        if (accounts?.[0]) {
            dispatch({ type: SET_IS_DEV, isDev: developerList.includes(accounts[0].username.toLowerCase()) });
        }
    }, [accounts]);

    useEffect(() => {
        dispatch({ type: SET_MOBILE, isMobile: window.innerWidth <= 768 });
    }, []);

    const handleWindowSizeChange = () => {
        dispatch({ type: SET_MOBILE, isMobile: window.innerWidth <= 768 });
    };

    useEffect(() => {
        window.addEventListener('resize', handleWindowSizeChange);
        return () => {
            window.removeEventListener('resize', handleWindowSizeChange);
        };
    }, []);

    useEffect(() => {
        if (error || successful) setOpen(true);
    }, [error, successful]);

    const { result } = useMsalAuthentication(InteractionType.Redirect, request);

    const id = useMemo<string>(() => {
        const tabId = sessionStorage.getItem('tabId');
        if (tabId) return tabId;

        const newTabId = crypto.randomUUID();
        sessionStorage.setItem('tabId', newTabId);
        return newTabId;
    }, []);

    useEffect(() => {
        if (result) {
            const { accessToken, idToken } = result;

            sessionStorage.setItem('ACCESS_TOKEN', accessToken);
            sessionStorage.setItem('ID_TOKEN', idToken);
            window.dispatchEvent(new Event("token"));
            try {
                sessionStorage.setItem('ACCESS_EXPIRY', (JSON.parse(Buffer.from(accessToken.split('.')[1], 'base64').toString('ascii')).exp * 1000).toString());
            } catch (err) {
                console.error(err);
            }
        }
    }, [result]);

    useEffect(() => {
        const getIdToken = async () => {
            try {
                const token = await instance.acquireTokenSilent({ ...request, account: accounts[0], forceRefresh: true });
                sessionStorage.setItem('ACCESS_TOKEN', token.accessToken);
                sessionStorage.setItem('ID_TOKEN', token.idToken);
            } catch (e) {
                console.error(e);
            }
        };

        getIdToken();
    }, [accounts, instance]);

    return (
        <>
            <Snackbar
                open={open}
                autoHideDuration={5000}
                onClose={((_e) => { dispatch(clearError()); dispatch(clearSuccessful()); setOpen(false); })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={successful ? "success" : "error"} color={successful ? "success" : "error"}>
                    {successful ? typeof successful === 'string' ? successful : JSON.stringify(successful) : error ? typeof error === 'string' ? error : JSON.stringify(error) : ''}
                </Alert>
            </Snackbar>
            <AuthenticatedTemplate>
                <Pages id={id} />
            </AuthenticatedTemplate>
            <UnauthenticatedTemplate />
        </>
    );
};

export default OperationsApp;
